import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { QRCodeCanvas } from "qrcode.react";
import { Copy, Check, Share2, Users, MousePointer2 } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "./ui/dialog";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { toast } from "sonner";
import { api } from "../lib/api";

export default function ShareAppModal({ open, onClose }) {
  const { t, i18n } = useTranslation();
  const isEn = i18n.language === "en";
  const dir = isEn ? "ltr" : "rtl";
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [copied, setCopied] = useState(false);

  // Always build the share URL from the current window origin so it works
  // on any domain (preview, torahya.com, custom domain) without needing a
  // backend env var. The backend's referral_code is the only piece we need.
  const shareUrl = data?.referral_code
    ? `${window.location.origin}/?ref=${data.referral_code}`
    : "";

  useEffect(() => {
    if (!open) return;
    setLoading(true);
    api
      .myReferral()
      .then((d) => setData(d))
      .catch(() => toast.error(t("share.load_error")))
      .finally(() => setLoading(false));
  }, [open, t]);

  const handleCopy = async () => {
    if (!shareUrl) return;
    try {
      await navigator.clipboard.writeText(shareUrl);
      setCopied(true);
      toast.success(t("share.link_copied"));
      setTimeout(() => setCopied(false), 2000);
    } catch {
      toast.error(t("share.copy_failed"));
    }
  };

  const handleNativeShare = async () => {
    if (!shareUrl) return;
    const text = t("share.native_share_text", { url: shareUrl });
    if (navigator.share) {
      try {
        await navigator.share({ title: "תוריא · Torahya", text, url: shareUrl });
      } catch {
        /* user cancelled */
      }
    } else {
      handleCopy();
    }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose?.()}>
      <DialogContent
        className="sm:max-w-md bg-card border-border/60"
        dir={dir}
        data-testid="share-app-modal"
      >
        <DialogHeader>
          <DialogTitle className="font-heading text-2xl text-gold flex items-center gap-2">
            <Share2 className="h-5 w-5" strokeWidth={1.5} />
            {t("share.modal_title")}
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-5 py-2">
          <p className="text-sm text-muted-foreground leading-relaxed">
            {t("share.modal_intro")}
          </p>

          {loading && (
            <div className="text-center py-12 text-muted-foreground" data-testid="share-loading">
              {t("share.loading")}
            </div>
          )}

          {!loading && data && (
            <>
              {/* QR */}
              <div className="flex justify-center">
                <div className="bg-white p-4 rounded-sm border border-border/60" data-testid="share-qr-wrapper">
                  <QRCodeCanvas
                    value={shareUrl}
                    size={200}
                    level="H"
                    includeMargin={false}
                    imageSettings={{
                      src: "/brand/icon-96.png",
                      height: 40,
                      width: 40,
                      excavate: true,
                    }}
                  />
                </div>
              </div>

              {/* Code */}
              <div className="text-center">
                <p className="text-xs uppercase tracking-wider text-muted-foreground mb-1">
                  {t("share.your_referral_code")}
                </p>
                <p
                  className="font-heading text-2xl tracking-[0.3em] text-gold"
                  data-testid="share-referral-code"
                >
                  {data.referral_code}
                </p>
              </div>

              {/* Share URL + copy */}
              <div className="flex items-center gap-2">
                <Input
                  readOnly
                  value={shareUrl}
                  data-testid="share-url-input"
                  className="text-xs ltr:font-mono"
                  dir="ltr"
                  onClick={(e) => e.target.select()}
                />
                <Button
                  onClick={handleCopy}
                  variant="outline"
                  size="sm"
                  data-testid="share-copy-btn"
                  className="shrink-0"
                >
                  {copied ? (
                    <Check className="h-4 w-4 text-emerald-500" strokeWidth={1.5} />
                  ) : (
                    <Copy className="h-4 w-4" strokeWidth={1.5} />
                  )}
                </Button>
              </div>

              {/* Stats */}
              <div className="grid grid-cols-2 gap-3 pt-2">
                <Stat
                  label={t("share.stat_clicks")}
                  value={data.clicks}
                  icon={MousePointer2}
                  testid="share-stat-clicks"
                />
                <Stat
                  label={t("share.stat_signups")}
                  value={data.signups}
                  icon={Users}
                  testid="share-stat-signups"
                />
              </div>

              {/* Native share */}
              <Button
                onClick={handleNativeShare}
                className="w-full sacred-btn"
                data-testid="share-native-btn"
              >
                <Share2 className="h-4 w-4 mr-2" strokeWidth={1.5} />
                {t("share.native_share")}
              </Button>
            </>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}

function Stat({ label, value, icon: Icon, testid }) {
  return (
    <div
      className="rounded-sm border border-border/60 bg-card/40 p-3 text-center"
      data-testid={testid}
    >
      <Icon className="h-4 w-4 mx-auto text-gold mb-1" strokeWidth={1.5} />
      <p className="font-heading text-xl text-foreground">{value}</p>
      <p className="text-[11px] text-muted-foreground mt-0.5">{label}</p>
    </div>
  );
}
